import React, { createContext, useContext, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Loader2, Check } from 'lucide-react';
import { launch } from '../mock/data';

const NotifyContext = createContext({ open: () => {} });
export const useNotify = () => useContext(NotifyContext);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotifyProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState('site');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | done

  const open = useCallback((src = 'site') => {
    setSource(src);
    setStatus('idle');
    setEmail('');
    setIsOpen(true);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source }),
      });
      if (!res.ok) throw new Error('bad');
      const data = await res.json();
      setStatus('done');
      toast.success(data.already ? "You're already on the list." : "You're on the list — we'll email you on drop day.");
    } catch {
      setStatus('idle');
      toast.error('Please enter a valid email and try again.');
    }
  };

  return (
    <NotifyContext.Provider value={{ open }}>
      {children}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent data-testid="notify-modal" className="sm:max-w-[420px] bg-white border-[var(--line)] shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase font-tight text-2xl tracking-wide">
              Get the drop alert
            </DialogTitle>
            <DialogDescription className="text-[var(--muted)]">
              Only 20 totes per color. We’ll email you the moment the Halloween drop goes live — delivery Oct 26, in-store + delivery Oct 30.
            </DialogDescription>
          </DialogHeader>

          {status === 'done' ? (
            <div data-testid="notify-success" className="flex flex-col items-center text-center py-6">
              <div className="h-12 w-12 rounded-full bg-[var(--accent)] flex items-center justify-center">
                <Check size={22} className="text-white" />
              </div>
              <p className="mt-4 font-display uppercase tracking-wide text-lg">You’re on the list</p>
              <p className="mt-1 text-sm text-[var(--muted)]">We’ll email you on drop day.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-2 flex flex-col gap-3">
              <Input
                data-testid="notify-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="h-12 text-base"
              />
              <button
                data-testid="notify-submit-button"
                type="submit"
                disabled={status === 'loading'}
                className="btn-primary h-12 inline-flex items-center justify-center gap-2 text-[13px] uppercase tracking-[0.14em] disabled:opacity-60"
              >
                {status === 'loading' ? (
                  <><Loader2 size={16} className="animate-spin" /> Joining…</>
                ) : (
                  'Notify me'
                )}
              </button>
              <p className="text-center text-[11px] text-[var(--muted-2)]">No spam. One email when it drops.</p>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </NotifyContext.Provider>
  );
};
